if (!customElements.get('cart-extra')) {
  const DISMISS_KEY = 'cart-extra-dismissed';

  const readDismissed = () => {
    try {
      return JSON.parse(sessionStorage.getItem(DISMISS_KEY)) || [];
    } catch (error) {
      return [];
    }
  };

  const writeDismissed = (ids) => {
    try {
      if (ids.length) sessionStorage.setItem(DISMISS_KEY, JSON.stringify(ids));
      else sessionStorage.removeItem(DISMISS_KEY);
    } catch (error) {}
  };

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  window.theme?.pubsub?.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event?.cart?.item_count === 0) writeDismissed([]);
  });

  customElements.define('cart-extra', class CartExtra extends HTMLElement {
    connectedCallback() {
      if (readDismissed().includes(this.dataset.productId)) this.hidden = true;
      if (this.ready) return;
      this.ready = true;
      this.sheet = this.querySelector('dialog');
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.onKeyup = (event) => {
        if (event.key !== 'Escape' || !this.sheet?.open) return;
        event.stopImmediatePropagation();
        this.sheet.close();
      };
      this.sheet?.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        event.stopPropagation();
      });
      this.sheet?.addEventListener('close', () => {
        window.removeEventListener('keyup', this.onKeyup, true);
        if (this.busy) return;
        this.querySelector('.cart-extra__body [data-extra-help]')?.focus({ preventScroll: true });
      });
    }

    disconnectedCallback() {
      window.removeEventListener('keyup', this.onKeyup, true);
    }

    get siblings() {
      return [...(this.parentElement?.querySelectorAll(':scope > cart-extra:not([hidden])') || [])];
    }

    onClick(event) {
      const target = event.target;
      if (target === this.sheet || target.closest('[data-extra-close]')) {
        this.sheet.close();
      } else if (target.closest('[data-extra-dismiss]')) {
        this.dismiss();
      } else if (target.closest('[data-extra-help]')) {
        this.openSheet(target.closest('[data-extra-help]'));
      } else if (target.closest('[data-extra-add]')) {
        this.add();
      }
    }

    onKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (this.sheet?.contains(event.target)) return;
      const cards = this.siblings;
      const next = cards[cards.indexOf(this) + (event.key === 'ArrowRight' ? 1 : -1)];
      if (!next) return;
      event.preventDefault();
      next.focusControl();
    }

    focusControl() {
      const control = this.querySelector('.cart-extra__body [data-extra-add]');
      control?.focus({ preventScroll: true });
      this.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
    }

    async dismiss() {
      const ids = readDismissed();
      if (!ids.includes(this.dataset.productId)) writeDismissed([...ids, this.dataset.productId]);
      if (this.sheet?.open) this.sheet.close();
      await this.leave();
    }

    async leave() {
      const cards = this.siblings;
      const index = cards.indexOf(this);
      const others = cards.filter((card) => card !== this);
      const animate = !reducedMotion() && typeof this.animate === 'function';

      if (animate) {
        await this.animate(
          [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(-6px) scale(0.98)' }],
          { duration: 160, easing: 'ease-in' }
        ).finished.catch(() => {});
      }

      const before = others.map((card) => card.getBoundingClientRect().left);
      this.hidden = true;
      if (animate) {
        others.forEach((card, i) => {
          const shift = before[i] - card.getBoundingClientRect().left;
          if (shift) card.animate([{ transform: `translateX(${shift}px)` }, { transform: 'none' }], { duration: 260, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' });
        });
      }

      const next = others[Math.min(index, others.length - 1)];
      if (next) {
        next.focusControl();
      } else {
        this.closest('cart-drawer, .cart-drawer, dialog, body')?.querySelector('[name="checkout"]')?.focus({ preventScroll: true });
      }
    }

    openSheet(trigger) {
      if (!this.sheet) return;
      this.sheet.style.top = '';
      this.sheet.style.left = '';
      this.sheet.showModal();
      window.addEventListener('keyup', this.onKeyup, true);
      if (!window.matchMedia('(min-width: 768px)').matches) return;
      const anchor = trigger.getBoundingClientRect();
      const sheet = this.sheet.getBoundingClientRect();
      const below = anchor.bottom + 8;
      const top = below + sheet.height > window.innerHeight - 16 ? Math.max(16, anchor.top - 8 - sheet.height) : below;
      const left = Math.min(Math.max(16, anchor.left), window.innerWidth - sheet.width - 16);
      this.sheet.style.top = `${top}px`;
      this.sheet.style.left = `${left}px`;
    }

    async add() {
      if (this.busy) return;
      this.busy = true;
      if (this.sheet?.open) this.sheet.close();
      this.setAttribute('aria-busy', 'true');

      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));
      const root = window.Shopify?.routes?.root || '/';

      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(this.dataset.variantId), quantity: 1 }],
            sections,
            sections_url: window.location.pathname
          })
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || 'Cart update failed');
        const cart = await theme.utils.getCartStateAfterMutation(result);
        await this.leave();
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-extra', cart });
      } catch (error) {
      } finally {
        this.busy = false;
        this.removeAttribute('aria-busy');
      }
    }
  });
}
